import { CSSProperties, useEffect } from "react";
import { Box, Typography, Button, Checkbox } from "@mui/material";
import { useAuthContext } from "../../../auth/useAuthContext";
import moment from "moment";

interface Props {
  typographyStyles?: CSSProperties;
  buttonStyles?: CSSProperties;
  tillApiData?: any;
  selectedDispenseTill?: any;
}

type OrderItem = {
  name: string;
  price: number;
  quantity: number;
};

type SummaryItem = {
  label: string;
  value: number;
};

const OrderSummary = ({
  typographyStyles,
  buttonStyles,
  tillApiData,
  selectedDispenseTill,
}: Props) => {
  // Add print styles when component mounts
  useEffect(() => {
    const printStyles = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-receipt, .print-receipt * {
          visibility: visible;
        }
        .print-receipt {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          max-width: none !important;
          border: none !important;
          box-shadow: none !important;
          margin: 0 !important;
          padding: 20px !important;
        }
        .print-receipt .no-print {
          display: none !important;
        }
        .print-receipt h6 {
          font-size: 24px !important;
          margin-bottom: 20px !important;
          text-align: center;
        }
        .print-receipt .order-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }
        .print-receipt .summary-section {
          border-top: 2px solid #000;
          margin-top: 15px;
          padding-top: 10px;
        }
        .print-receipt .total-section {
          border-top: 3px double #000;
          margin-top: 15px;
          padding-top: 10px;
          font-weight: bold;
        }
        .print-receipt .dispense-info {
          text-align: center;
          margin-bottom: 15px;
          font-size: 12px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }
      }
    `;

    const styleSheet = document.createElement("style");
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const { user } = useAuthContext();

  // Generate order items based on selected dispense till
  const generateOrderItems = (): OrderItem[] => {
    // If no dispense till is selected, show placeholder
    if (!selectedDispenseTill) {
      return [{ name: "No dispense till selected", price: 0, quantity: 0 }];
    }

    // For patient users with selected dispense till, show mock detailed items
    if (user?.roleId === "patient" && selectedDispenseTill) {
      // Generate mock items based on the dispense amount
      const baseAmount = selectedDispenseTill.amount;

      // Create realistic breakdown of the total amount
      if (baseAmount >= 500) {
        return [
          {
            name: "Prescription Glasses Frame",
            price: Math.floor(baseAmount * 0.4),
            quantity: 1,
          },
          {
            name: "Premium Lens Pair",
            price: Math.floor(baseAmount * 0.35),
            quantity: 1,
          },
          {
            name: "Anti-Glare Coating",
            price: Math.floor(baseAmount * 0.15),
            quantity: 1,
          },
          {
            name: "Blue Light Filter",
            price: Math.floor(baseAmount * 0.1),
            quantity: 1,
          },
        ];
      } else {
        return [
          {
            name: `Dispense Item #${selectedDispenseTill.dispenseId}`,
            price: baseAmount,
            quantity: 1,
          },
        ];
      }
    }

    // For non-patient users or default case
    return [
      {
        name: `Dispense #${selectedDispenseTill.dispenseId} - ${selectedDispenseTill.status}`,
        price: selectedDispenseTill.amount,
        quantity: 1,
      },
    ];
  };

  const orderItems = generateOrderItems();

  // Calculate totals based on selected dispense till
  const subtotal = selectedDispenseTill ? selectedDispenseTill.amount : 0;

  // Apply discounts and fees only if there's a selected dispense till with amount > 0
  const discount =
    subtotal > 0 && user?.roleId === "patient"
      ? Math.floor(subtotal * -0.05)
      : 0; // 5% discount for patients
  const shippingFee = subtotal > 0 ? 0 : 0; // No shipping for prescriptions
  const voucher = 0; // No voucher for now
  const total = subtotal + discount + shippingFee + voucher;

  // Calculate payment status
  const payments = selectedDispenseTill?.payments || [];
  const totalPaid = payments.reduce((sum: number, p: any) => sum + p.amount, 0);

  const amountPaid =
    totalPaid > 0
      ? totalPaid
      : selectedDispenseTill?.status === "paid"
        ? selectedDispenseTill?.amount || 0
        : 0;

  const outstandingBalance = total - amountPaid;

  const orderSummary: SummaryItem[] = [
    { label: "Subtotal:", value: subtotal },
    ...(discount !== 0 ? [{ label: "Discount:", value: discount }] : []),
    ...(shippingFee !== 0
      ? [{ label: "Shipping Fee:", value: shippingFee }]
      : []),
    ...(voucher !== 0 ? [{ label: "Voucher:", value: voucher }] : []),
  ];

  const orderTotals: SummaryItem[] = [
    { label: "Total Amount:", value: total },
    ...(amountPaid > 0 ? [{ label: "Amount Paid:", value: amountPaid }] : []),
    { label: "Outstanding Balance:", value: outstandingBalance },
  ];

  return (
    <Box
      className="print-receipt"
      sx={(theme) => ({
        border: selectedDispenseTill
          ? selectedDispenseTill.status === "pending"
            ? `2px solid ${theme.palette.warning.main}`
            : `2px solid ${theme.palette.success.main}`
          : `2px solid ${theme.palette.grey[500]}`,
        borderRadius: "8px",
        padding: 4,
        maxWidth: "500px",
        position: "relative",
        backgroundColor: theme.palette.background.paper,
        width: "35%",
        fontFamily: "Public Sans",
      })}
    >
      {/* Dispense Till Info Section for Print */}
      {selectedDispenseTill && (
        <Box className="dispense-info">
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Till ID: {tillApiData?.till?.id} | Dispense ID: #
            {selectedDispenseTill.dispenseId}
          </Typography>
          <Typography variant="caption">
            Status: {selectedDispenseTill.status.toUpperCase()} |
            {selectedDispenseTill.paymentDate
              ? ` Paid: ${moment(selectedDispenseTill.paymentDate).format(
                  "DD/MM/YYYY HH:mm",
                )}`
              : " Payment Pending"}
          </Typography>
          {selectedDispenseTill.paymentMethod && (
            <Typography variant="caption">
              <br />
              Payment Method: {selectedDispenseTill.paymentMethod}
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="h6" fontWeight="bold">
        {selectedDispenseTill
          ? `Dispense Order #${selectedDispenseTill.dispenseId}`
          : "Order Summary"}
      </Typography>

      {/* Order Status Indicator */}
      {selectedDispenseTill && (
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1,
            borderRadius: 1,
            bgcolor:
              theme.palette.mode === "dark"
                ? theme.palette.background.neutral
                : selectedDispenseTill.status === "pending"
                  ? "#fff3e0"
                  : "#e8f5e8",
          })}
        >
          <Typography
            variant="body2"
            sx={(theme) => ({
              fontWeight: "bold",
              textAlign: "center",
              color:
                selectedDispenseTill.status === "pending"
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
            })}
          >
            {selectedDispenseTill.status === "pending"
              ? "Payment Pending"
              : "Payment Completed"}
          </Typography>
        </Box>
      )}

      <Box sx={{ marginTop: 1 }}>
        {orderItems.map((item, index) => (
          <Typography
            key={index}
            className="order-item"
            sx={{
              ...typographyStyles,
              color: "text.primary",
              "& span": {
                color: "text.primary",
              },
            }}
          >
            <span>
              {item.quantity > 0
                ? `${item.quantity} x ${item.name}`
                : item.name}
            </span>

            <span>£{item.price * item.quantity}</span>
          </Typography>
        ))}
      </Box>

      {orderSummary.length > 1 && (
        <Box
          className="summary-section"
          sx={(theme) => ({
            marginTop: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            paddingTop: 1,
          })}
        >
          {orderSummary.map((summary, index) => (
            <Typography
              key={index}
              className="order-item"
              sx={{
                ...typographyStyles,
                color: "text.primary",
                "& span": {
                  color: "text.primary",
                },
              }}
            >
              <span>{summary.label}</span>
              <span>£{summary.value}</span>
            </Typography>
          ))}
        </Box>
      )}

      <Box
        className="total-section"
        sx={{ marginTop: 2, borderTop: "1px solid #ccc", paddingTop: 1 }}
      >
        {orderTotals.map((total, index) => (
          <Typography key={index} className="order-item" sx={typographyStyles}>
            <Box
              component="span"
              sx={(theme) => ({
                fontWeight: 500,
                color: theme.palette.text.primary,
              })}
            >
              {total.label}
            </Box>

            <Box
              component="span"
              sx={(theme) => ({
                fontWeight:
                  total.label === "Outstanding Balance:" ? "bold" : "normal",
                color:
                  total.label === "Outstanding Balance:" && total.value > 0
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
              })}
            >
              £{total.value}
            </Box>
          </Typography>
        ))}
      </Box>

      {/* Wallet Balance Info */}
      {tillApiData && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            bgcolor: "background.neutral",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              fontSize: "12px",
              color: "text.primary",
            }}
          >
            Wallet Balance: £{tillApiData.till.wallet}
          </Typography>
        </Box>
      )}

      {/* Payment Status Message */}
      {selectedDispenseTill && (
        <Box sx={{ mt: 2 }}>
          {selectedDispenseTill.status === "pending" ? (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#f57c00",
                fontStyle: "italic",
                fontSize: "12px",
              }}
            >
              This order requires payment to complete
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                color: "#388e3c",
                fontStyle: "italic",
                fontSize: "12px",
              }}
            >
              Payment completed{" "}
              {selectedDispenseTill.paymentDate
                ? `on ${moment(selectedDispenseTill.paymentDate).format(
                    "DD/MM/YYYY",
                  )}`
                : ""}
            </Typography>
          )}
        </Box>
      )}

      <Box
        className="no-print"
        sx={{ mt: 16, display: "flex", alignItems: "center" }}
      >
        {/* <Typography
          sx={{ fontWeight: "100", fontSize: "12px", color: "black" }}>
          Send Receipt on Email
        </Typography> */}
        {/* <Checkbox defaultChecked color="default" size="small" /> */}
      </Box>

      <Box
        className="no-print"
        sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
      >
        <Button
          variant="outlined"
          sx={{
            ...buttonStyles,
            opacity: selectedDispenseTill ? 1 : 0.5,
          }}
          onClick={() => window.print()}
          disabled={!selectedDispenseTill}
        >
          Print Receipt
        </Button>
      </Box>
    </Box>
  );
};

export default OrderSummary;
